import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import axios from 'axios';
import {
    FaEye, FaTrashAlt, FaTimes, FaSearch,
    FaFileExcel, FaAngleDoubleRight, FaCheckDouble, FaExchangeAlt,
    FaChevronLeft, FaChevronRight, FaCheckSquare, FaRegSquare, FaChevronDown, FaChevronUp,
    FaPrint
} from 'react-icons/fa';
import './ViewAdmissionForm.css';
import logo from '../../assets/logo.png'; 

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- HELPER: Display Checkbox ---
const CheckboxDisplay = ({ label, checked }) => (
    <div className="view-admission-form-sk-checkbox-display">
        {checked ? <FaCheckSquare className="view-admission-form-sk-icon-checked" /> : <FaRegSquare className="view-admission-form-sk-icon-unchecked" />}
        <span>{label}</span>
    </div>
);

// --- VIEW MODAL ---
const ViewModal = ({ data, onClose }) => {
    
    // --- 1. HOOKS MUST BE AT THE TOP (Before any return statement) ---
    useEffect(() => {
        if (data) {
            // Add class when data exists (modal is open)
            document.body.classList.add('view-admission-form-sk-print-active');
        } else {
            // Remove class if data is null (modal is closed)
            document.body.classList.remove('view-admission-form-sk-print-active');
        }

        // Cleanup function: always remove class when component unmounts or data changes
        return () => {
            document.body.classList.remove('view-admission-form-sk-print-active');
        };
    }, [data]);

    // --- 2. EARLY RETURN CHECK ---
    if (!data) return null;

    // --- 3. COMPONENT LOGIC ---
    const getFileUrl = (relativePath) => {
        if (!relativePath) return null;
        const cleanPath = relativePath.replace(/\\/g, '/');
        return `${API_BASE_URL}/${cleanPath}`;
    };

    // Parse Optionals safely
    let optionalSubjects = [];
    try {
        optionalSubjects = Array.isArray(data.optionalSubjects) ? data.optionalSubjects : JSON.parse(data.optionalSubjects || '[]');
    } catch (e) {
        console.error("Failed to parse optional subjects:", e);
        optionalSubjects = [];
    }
    
    // --- PRINT HANDLER ---
    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="view-admission-form-sk-modal-overlay">
            <div className="view-admission-form-sk-modal-content">
                
                <div className="view-admission-form-sk-modal-header view-admission-form-sk-no-print">
                    <h3>Admission Form Details</h3>
                    <div className="view-admission-form-sk-modal-header-actions">
                         <button className="view-admission-form-sk-print-btn" onClick={handlePrint} title="Print Form">
                            <FaPrint />
                        </button>
                        <button className="view-admission-form-sk-close-btn" onClick={onClose}><FaTimes /></button>
                    </div>
                </div>

                <div className="view-admission-form-sk-modal-body" id="view-admission-form-sk-printable-area">
                    <div>
                        {/* 1. HEADER SECTION */}
                        <div className="view-admission-form-sk-header-section">
                            <div className="view-admission-form-sk-header-flex">
                                <img src={logo} alt="Logo" className="view-admission-form-sk-logo" />
                                <div className="view-admission-form-sk-header-text">
                                    <h1 className="view-admission-form-sk-hindi-title">सुचेता कृपलानी महाविद्यालय</h1>
                                    <h2 className="view-admission-form-sk-eng-title">S.K. College</h2>
                                    <p className="view-admission-form-sk-affiliation">जय नारायण व्यास विश्वविद्यालय, जोधपुर (JNVU), राजस्थान से सम्बद्ध (Affiliated) है।</p>
                                    <p className="view-admission-form-sk-address">पता :- ग्रा. पो. – मानकलाव, जोधपुर, 342305 राज.</p>
                                </div>
                            </div>
                            <div className="view-admission-form-sk-form-title-row">
                                <h3>प्रवेश आवेदन पत्र</h3>
                            </div>
                            <div className="view-admission-form-sk-session-container">
                                <span>सत्र 20</span>
                                <span className="view-admission-form-sk-session-val">{data.sessionStart || '25'}</span>
                                <span>- 20</span>
                                <span className="view-admission-form-sk-session-val">{data.sessionEnd || '26'}</span>
                            </div>
                        </div>

                        {/* 2. CONTENT SECTIONS */}
                        <div className="view-admission-form-sk-view-content">
                            
                            {/* Student Details */}
                            <div className="view-admission-form-sk-section">
                                <h4 className="view-admission-form-sk-section-title">विद्यार्थी विवरण (Student Details)</h4>
                                <div className="view-admission-form-sk-student-layout">
                                    <div className="view-admission-form-sk-photo-box">
                                        {data.photo ? <img src={getFileUrl(data.photo)} alt="Student" /> : <span>PHOTO</span>}
                                    </div>
                                    <div className="view-admission-form-sk-info-grid">
                                        <div className="view-admission-form-sk-row">
                                            <div className="view-admission-form-sk-field"><label>विद्यार्थी का नाम (Student Name)</label><div className="view-admission-form-sk-val">{data.studentName}</div></div>
                                            <div className="view-admission-form-sk-field"><label>जन्म तिथि / DOB</label><div className="view-admission-form-sk-val">{data.dob}</div></div>
                                        </div>
                                        <div className="view-admission-form-sk-row">
                                            <div className="view-admission-form-sk-field"><label>पिता का नाम / Father's Name</label><div className="view-admission-form-sk-val">{data.fatherName}</div></div>
                                            <div className="view-admission-form-sk-field"><label>माता का नाम / Mother's Name</label><div className="view-admission-form-sk-val">{data.motherName}</div></div>
                                        </div>
                                        <div className="view-admission-form-sk-row three-col">
                                            <div className="view-admission-form-sk-field"><label>श्रेणी (Category)</label><div className="view-admission-form-sk-val">{data.category}</div></div>
                                            <div className="view-admission-form-sk-field"><label>जाति / Caste</label><div className="view-admission-form-sk-val">{data.caste}</div></div>
                                            <div className="view-admission-form-sk-field"><label>लिंग / Gender</label><div className="view-admission-form-sk-val">{data.gender}</div></div>
                                        </div>
                                        <div className="view-admission-form-sk-row">
                                            <div className="view-admission-form-sk-field"><label>आधार क्रमांक / Aadhar Number</label><div className="view-admission-form-sk-val">{data.aadharNumber}</div></div>
                                            <div className="view-admission-form-sk-field"><label>धर्म / Religion</label><div className="view-admission-form-sk-val">{data.religion}</div></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div className="view-admission-form-sk-section">
                                <h4 className="view-admission-form-sk-section-title">संपर्क जानकारी (Contact Details)</h4>
                                <div className="view-admission-form-sk-row"><div className="view-admission-form-sk-field full"><label>1. स्थायी पता / Permanent Address</label><div className="view-admission-form-sk-val">{data.permanentAddress}</div></div></div>
                                <div className="view-admission-form-sk-row"><div className="view-admission-form-sk-field full"><label>2. वर्तमान पता / Current Address</label><div className="view-admission-form-sk-val">{data.currentAddress}</div></div></div>
                                <div className="view-admission-form-sk-row">
                                    <div className="view-admission-form-sk-field"><label>3. मोबाइल नंबर / Mobile Number</label><div className="view-admission-form-sk-val">{data.mobile}</div></div>
                                    <div className="view-admission-form-sk-field"><label>4. ईमेल आईडी / Email ID</label><div className="view-admission-form-sk-val">{data.email}</div></div>
                                </div>
                            </div>

                            {/* Educational Qualification */}
                            <div className="view-admission-form-sk-section">
                                <h4 className="view-admission-form-sk-section-title">शैक्षणिक योग्यता (Educational Qualification)</h4>
                                <div className="view-admission-form-sk-row">
                                    <div className="view-admission-form-sk-field"><label>कक्षा जिसमें प्रवेश लेना चाहते हैं / Class for Admission</label><div className="view-admission-form-sk-val">{data.admissionClass}</div></div>
                                    <div className="view-admission-form-sk-field"><label>अंतिम उत्तीर्ण परीक्षा का नाम / Last Passed Exam</label><div className="view-admission-form-sk-val">{data.lastExamName}</div></div>
                                </div>
                                <div className="view-admission-form-sk-row">
                                    <div className="view-admission-form-sk-field"><label> गत परीक्षा में आप / Status in Previous Exam</label><div className="view-admission-form-sk-val">{data.studentStatus}</div></div>
                                    <div className="view-admission-form-sk-field"><label> अंतिम संस्था जहाँ अध्ययन किया / Last Institution</label><div className="view-admission-form-sk-val">{data.lastInstitution}</div></div>
                                </div>
                            </div>

                            {/* Result */}
                            <div className="view-admission-form-sk-section">
                                <h4 className="view-admission-form-sk-section-title">अंतिम परीक्षा परिणाम का विवरण (Result Details)</h4>
                                <div className="view-admission-form-sk-row three-col">
                                    <div className="view-admission-form-sk-field"><label>पूर्णांक / Total Marks</label><div className="view-admission-form-sk-val">{data.totalMarks}</div></div>
                                    <div className="view-admission-form-sk-field"><label>प्राप्तांक / Marks Obtained</label><div className="view-admission-form-sk-val">{data.obtainedMarks}</div></div>
                                    <div className="view-admission-form-sk-field"><label>प्रतिशत / Percentage</label><div className="view-admission-form-sk-val">{data.percentage}%</div></div>
                                </div>
                            </div>

                            {/* Subject Selection */}
                            <div className="view-admission-form-sk-section">
                                <h4 className="view-admission-form-sk-section-title">विषय चयन (Subject Selection)</h4>
                                <div className="view-admission-form-sk-subject-boxes">
                                    <div className="view-admission-form-sk-box"><label>अनिवार्य विषय (Compulsory Subject)</label><div className="view-admission-form-sk-box-opts"><CheckboxDisplay label="Hindi" checked={data.compulsorySubject === 'Hindi'} /><CheckboxDisplay label="English" checked={data.compulsorySubject === 'English'} /></div></div>
                                    <div className="view-admission-form-sk-box"><label>माध्यम (Medium)</label><div className="view-admission-form-sk-box-opts"><CheckboxDisplay label="Hindi" checked={data.medium === 'Hindi'} /><CheckboxDisplay label="English" checked={data.medium === 'English'} /></div></div>
                                </div>
                                <div className="view-admission-form-sk-optional-row">
                                    <strong>ऐच्छिक विषय (Optional Subjects):</strong>
                                    <span>{optionalSubjects.length > 0 ? optionalSubjects.join(', ') : 'None'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MAIN ADMIN PANEL ---
const AdminAdmissions = () => {
    const [admissions, setAdmissions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [viewingData, setViewingData] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchAdmissions = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admissions/all`);
            setAdmissions(res.data);
        } catch (error) {
            console.error("Fetch error:", error);
            Swal.fire('Error', 'Could not fetch data.', 'error');
        }
    };

    useEffect(() => { fetchAdmissions(); }, []);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const filteredData = useMemo(() => {
        return admissions.filter(item => {
            if (statusFilter !== 'All' && item.status !== statusFilter) return false;
            const term = searchTerm.toLowerCase();
            return (
                item.studentName?.toLowerCase().includes(term) ||
                item.mobile?.includes(term) ||
                item.email?.toLowerCase().includes(term) ||
                item.admissionClass?.toLowerCase().includes(term)
            );
        });
    }, [admissions, searchTerm, statusFilter]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    const updateStatusAPI = async (id, status) => {
        try {
            const updateData = { status };
            if(status === 'Approved' || status === 'Rejected') {
                updateData.resolvedOn = new Date();
            }
            const res = await axios.put(`${API_BASE_URL}/api/admissions/status/${id}`, updateData);
            setAdmissions(prev => prev.map(item => item._id === id ? res.data : item));
            return true;
        } catch (error) {
            Swal.fire('Error', 'Action failed', 'error');
            return false;
        }
    };

    const handleStartProcess = (id) => Swal.fire({ title: 'Start Processing?', icon: 'question', showCancelButton: true, confirmButtonText: 'Yes' }).then(r => { if(r.isConfirmed) updateStatusAPI(id, 'In Progress'); });
    const handleDecision = (id) => Swal.fire({ title: 'Decision', showDenyButton: true, showCancelButton: true, confirmButtonText: 'Approve', denyButtonText: 'Reject' }).then(r => { if(r.isConfirmed) updateStatusAPI(id, 'Approved'); else if(r.isDenied) updateStatusAPI(id, 'Rejected'); });
    const handleChangeStatus = (item) => { const newStatus = item.status === 'Approved' ? 'Rejected' : 'Approved'; Swal.fire({ title: `Change to ${newStatus}?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes' }).then(r => { if(r.isConfirmed) updateStatusAPI(item._id, newStatus); }); };
    const handleDelete = (id) => Swal.fire({ title: 'Delete?', text: "Cannot be undone!", icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33' }).then(async r => { if (r.isConfirmed) { try { await axios.delete(`${API_BASE_URL}/api/admissions/delete/${id}`); setAdmissions(prev => prev.filter(i => i._id !== id)); Swal.fire('Deleted!', '', 'success'); } catch (e) { Swal.fire('Error', 'Delete failed', 'error'); } } });

    // --- DESIGNED EXCEL EXPORT (USING EXCELJS & FILE-SAVER) ---
    const handleExport = async () => {
        if (filteredData.length === 0) {
            return Swal.fire('Notice', 'No records available to export.', 'info');
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Admissions Database');

        // Main Brand Title Row
        worksheet.mergeCells('A1:Y1');
        worksheet.getCell('A1').value = 'SUCHETA KRIPLANI MAHAVIDYALAYA (S.K. COLLEGE)';
        worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FF1E3A8A' } };
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

        // Subtitle Row
        worksheet.mergeCells('A2:Y2');
        worksheet.getCell('A2').value = 'Admission Directory & Student Profiles (प्रवेश आवेदन सूची)';
        worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
        worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

        // Configuration Information Row
        worksheet.mergeCells('A3:Y3');
        worksheet.getCell('A3').value = `Status Filter: ${statusFilter}  |  Search Filter: ${searchTerm || 'None'}  |  Exported Date: ${new Date().toLocaleDateString('en-IN')}`;
        worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
        worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.addRow([]); // Blank spacer row

        // Database Column Headers
        const headers = [
            'S.No', 'Student Name', "Father's Name", "Mother's Name", 'Date of Birth', 'Gender', 'Category', 
            'Caste', 'Religion', 'Mobile Number', 'Email ID', 'Permanent Address', 'Current Address', 
            'Admission Class', 'Last Passed Exam', 'Last Institution', 'Total Marks', 'Obtained Marks', 
            'Percentage (%)', 'Compulsory Subject', 'Medium', 'Optional Subjects', 'Status', 
            'Submitted On', 'Resolved On'
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2C3E50' } };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Arial', size: 10 };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // Populate Database Rows
        filteredData.forEach((c, i) => {
            let optionalSubjectsString = '—';
            try {
                const parsed = Array.isArray(c.optionalSubjects) ? c.optionalSubjects : JSON.parse(c.optionalSubjects || '[]');
                if (parsed.length > 0) {
                    optionalSubjectsString = parsed.join(', ');
                }
            } catch (e) {
                optionalSubjectsString = c.optionalSubjects || '—';
            }

            const row = worksheet.addRow([
                i + 1,
                c.studentName || '—',
                c.fatherName || '—',
                c.motherName || '—',
                c.dob || '—',
                c.gender || '—',
                c.category || '—',
                c.caste || '—',
                c.religion || '—',
                c.mobile || '—',
                c.email || '—',
                c.permanentAddress || '—',
                c.currentAddress || '—',
                c.admissionClass || '—',
                c.lastExamName || '—',
                c.lastInstitution || '—',
                c.totalMarks !== undefined ? c.totalMarks : '—',
                c.obtainedMarks !== undefined ? c.obtainedMarks : '—',
                c.percentage !== undefined ? `${c.percentage}%` : '—',
                c.compulsorySubject || '—',
                c.medium || '—',
                optionalSubjectsString,
                c.status || '—',
                formatDate(c.submittedOn),
                formatDate(c.resolvedOn)
            ]);

            row.eachCell((cell, colIndex) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.font = { name: 'Arial', size: 9 };

                // Handle conditional alignment mapping
                // Left alignment for text/paragraph descriptors
                if ([2, 3, 4, 11, 12, 13, 16, 22].includes(colIndex)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                }
            });

            // Set specific colors for the status column (Column 23)
            const statusCell = row.getCell(23);
            let statusColor = 'FF000000'; // Default Black
            if (c.status === 'Approved') statusColor = 'FF16A34A'; // Green
            else if (c.status === 'Rejected') statusColor = 'FFDC2626'; // Red
            else if (c.status === 'In Progress') statusColor = 'FFD97706'; // Amber/Orange
            else if (c.status === 'Open') statusColor = 'FF2563EB'; // Blue

            statusCell.font = { bold: true, name: 'Arial', size: 9, color: { argb: statusColor } };
        });

        // Set structural column widths
        worksheet.columns = [
            { width: 8 },   // S.No
            { width: 22 },  // Student Name
            { width: 22 },  // Father's Name
            { width: 22 },  // Mother's Name
            { width: 14 },  // Date of Birth
            { width: 12 },  // Gender
            { width: 12 },  // Category
            { width: 14 },  // Caste
            { width: 12 },  // Religion
            { width: 15 },  // Mobile Number
            { width: 25 },  // Email ID
            { width: 35 },  // Permanent Address
            { width: 35 },  // Current Address
            { width: 18 },  // Admission Class
            { width: 20 },  // Last Passed Exam
            { width: 25 },  // Last Institution
            { width: 12 },  // Total Marks
            { width: 12 },  // Obtained Marks
            { width: 14 },  // Percentage (%)
            { width: 18 },  // Compulsory Subject
            { width: 12 },  // Medium
            { width: 25 },  // Optional Subjects
            { width: 15 },  // Status
            { width: 20 },  // Submitted On
            { width: 20 }   // Resolved On
        ];

        // Process final write stream & trigger file save
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'SK_College_Admissions_Database.xlsx');
    };

    return (
        <div className="view-admission-form-sk-wrapper">
            <div className="view-admission-form-sk-container">
                <ViewModal data={viewingData} onClose={() => setViewingData(null)} />
                
                <h1 className="view-admission-form-sk-page-heading">Admission Management</h1>
                
                <div className="view-admission-form-sk-controls-card">
                    <div className="view-admission-form-sk-controls-row">
                        <div className="view-admission-form-sk-search-box">
                            <FaSearch />
                            <input type="text" placeholder="Search name, mobile..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                        <div className="view-admission-form-sk-actions-right">
                            <div className="view-admission-form-sk-pagination-size">
                                <span>Show</span>
                                <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                    <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                                </select>
                                <span>entries</span>
                            </div>
                            <select className="view-admission-form-sk-status-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="All">All Status</option><option value="Open">Open</option><option value="In Progress">In Progress</option><option value="Approved">Approved</option><option value="Rejected">Rejected</option>
                            </select>
                            <button className="view-admission-form-sk-export-btn" onClick={handleExport}><FaFileExcel /> Export All</button>
                        </div>
                    </div>
                </div>

                <div className="view-admission-form-sk-table-card">
                    <div className="view-admission-form-sk-table-wrapper">
                        <table className="view-admission-form-sk-table">
                            <thead>
                                <tr>
                                    <th className="col-sno">S.No</th><th className="col-name">Student Name</th><th className="col-contact">Contact Number</th>
                                    <th className="col-email">Email ID</th><th className="col-class">कक्षा</th><th className="col-submit">Submitted On</th>
                                    <th className="col-status">Status</th><th className="col-resolve">Resolved On</th><th className="col-action">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (currentItems.map((item, index) => (
                                    <React.Fragment key={item._id}>
                                        <tr className={item.status === 'Open' ? 'view-admission-form-sk-new' : ''}>
                                            <td className="col-sno">{indexOfFirstItem + index + 1}</td>
                                            <td className="col-name view-admission-form-sk-name-cell">
                                                <span>{item.studentName}</span>
                                                <button className="view-admission-form-sk-toggle-btn" onClick={() => toggleRow(item._id)}>
                                                    {expandedRow === item._id ? <FaChevronUp /> : <FaChevronDown />}
                                                </button>
                                            </td>
                                            <td className="col-contact">{item.mobile}</td>
                                            <td className="col-email">{item.email}</td>
                                            <td className="col-class">{item.admissionClass}</td>
                                            <td className="col-submit">{formatDate(item.submittedOn)}</td>
                                            <td className="col-status"><span className={`view-admission-form-sk-status-badge ${item.status.toLowerCase().replace(' ', '-')}`}>{item.status}</span></td>
                                            <td className="col-resolve">{item.resolvedOn ? formatDate(item.resolvedOn) : '-'}</td>
                                            <td className="col-action">
                                                <div className="view-admission-form-sk-actions">
                                                    <button className="view-admission-form-sk-btn view" onClick={() => setViewingData(item)} title="View Details"><FaEye /></button>
                                                    {item.status === 'Open' && <button className="view-admission-form-sk-btn process" onClick={() => handleStartProcess(item._id)}><FaAngleDoubleRight /></button>}
                                                    {item.status === 'In Progress' && <button className="view-admission-form-sk-btn decision" onClick={() => handleDecision(item._id)}><FaCheckDouble /></button>}
                                                    {(item.status === 'Approved' || item.status === 'Rejected') && (<>
                                                        <button className="view-admission-form-sk-btn change" onClick={() => handleChangeStatus(item)}><FaExchangeAlt /></button>
                                                        <button className="view-admission-form-sk-btn delete" onClick={() => handleDelete(item._id)}><FaTrashAlt /></button>
                                                    </>)}
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRow === item._id && (
                                            <tr className="view-admission-form-sk-expand-row">
                                                <td colSpan="9">
                                                    <div className="view-admission-form-sk-expand-grid">
                                                        <div className="exp-item exp-contact-item exp-responsive"><strong>Contact Number:</strong> <span>{item.mobile}</span></div>
                                                        <div className="exp-item exp-email-item exp-responsive"><strong>Email:</strong> <span>{item.email}</span></div>
                                                        <div className="exp-item exp-class-item exp-responsive"><strong>Class:</strong> <span>{item.admissionClass}</span></div>
                                                        <div className="exp-item exp-status-item exp-responsive"><strong>Status:</strong> <span className={`view-admission-form-sk-status-badge ${item.status.toLowerCase().replace(' ', '-')}`}>{item.status}</span></div>
                                                        <div className="exp-item exp-submit-item exp-responsive"><strong>Submitted On:</strong> <span>{formatDate(item.submittedOn)}</span></div>
                                                        <div className="exp-item exp-resolve-item exp-responsive"><strong>Resolved On:</strong> <span>{item.resolvedOn ? formatDate(item.resolvedOn) : '-'}</span></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))) : (
                                    <tr><td colSpan="9" style={{textAlign: 'center', padding: '2rem'}}>No records found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="view-admission-form-sk-pagination-footer">
                    <div className="view-admission-form-sk-page-info">
                        Showing {currentItems.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
                    </div>
                    <div className="view-admission-form-sk-page-nav">
                        <button className="view-admission-form-sk-page-btn" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                            <FaChevronLeft /> Previous
                        </button>
                        <span className="view-admission-form-sk-page-count">Page {currentPage} of {totalPages || 1}</span>
                        <button className="view-admission-form-sk-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => handlePageChange(currentPage + 1)}>
                            Next <FaChevronRight />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminAdmissions;